import { useEffect, useRef } from "react";
import {
    Box,
    Camera,
    FileUp,
    Plus,
    RotateCcw,
    Settings2,
    Share2,
    Shapes,
    Trash2,
} from "lucide-react";
import { usePrimitivePoints } from "./PrimitivePointsContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import { isValidSvg } from "../../utils/svgUtils";
import { roundDeg } from "../../utils/primitivePointsPlacement";

export function PrimitivePointsControls() {
    const {
        primitiveType,
        subdivisions,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraFovDeg,
        cameraDistance,
        viewBoxX,
        viewBoxY,
        viewBoxWidth,
        viewBoxHeight,
        backfaceCulling,
        objectPlacementMode,
        objectRotationMode,
        objectSvgs,
        scale,
        depthScaleEnabled,
        depthScaleStrength,
        baseRotationDeg,
        positionOffsetRandomness,
        flipXPercent,
        flipYPercent,
        flipOnBackfacesOnly,
        rotationRandomnessDeg,
        groupByFaceOnExport,
        polarRotationCenterXPct,
        polarRotationCenterYPct,
        anchor,
        error,
        objectInputRefs,
        setPrimitiveType,
        setSubdivisions,
        setRotationXDeg,
        setRotationYDeg,
        setRotationZDeg,
        setCameraFovDeg,
        setCameraDistance,
        setViewBoxX,
        setViewBoxY,
        setViewBoxWidth,
        setViewBoxHeight,
        setBackfaceCulling,
        setObjectPlacementMode,
        setObjectRotationMode,
        setScale,
        setDepthScaleEnabled,
        setDepthScaleStrength,
        setBaseRotationDeg,
        setPositionOffsetRandomness,
        setFlipXPercent,
        setFlipYPercent,
        setFlipOnBackfacesOnly,
        setRotationRandomnessDeg,
        setGroupByFaceOnExport,
        setPolarRotationCenterXPct,
        setPolarRotationCenterYPct,
        setAnchor,
        addObjectSvg,
        removeObjectSvg,
        handleObjectFileLoad,
        canExport,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = usePrimitivePoints();
    const colorTimeoutRefs = useRef<Map<number, ReturnType<typeof setTimeout>>>(
        new Map(),
    );
    useEffect(() => {
        return () => {
            colorTimeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
            colorTimeoutRefs.current.clear();
        };
    }, []);

    const handleInt =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleRotationDeg =
        (setter: (v: number) => void, min = -3600, max = 3600) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(roundDeg(n));
        };

    const importObjectFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text = typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) return;
                addObjectSvg({
                    name: file.name,
                    content: text,
                });
            };
            reader.readAsText(file);
        }
    };

    return (
        <div className="panel-controls path-populate-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Primitive Points</h2>
                    <ToolDescriptionPopover dialogLabel="Primitive Points tool description">
                        <p>
                            Subdivide primitive faces in 3D, project vertices through
                            a camera, and stamp your object SVG at each visible point.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="primitive-points"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Object SVGs" icon={Shapes}>
                    <p className="path-populate-preset-hint">Quick add preset</p>
                    <div className="path-populate-preset-chips">
                        {OBJECT_SVG_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                                title={`Add ${preset.label} as object`}
                                aria-label={`Add ${preset.label} as object`}
                                onClick={() => {
                                    if (!isValidSvg(preset.svg)) return;
                                    addObjectSvg({
                                        name: `${preset.label} (preset)`,
                                        content: preset.svg,
                                    });
                                }}
                            >
                                <span
                                    className="path-populate-preset-chip-icon"
                                    aria-hidden="true"
                                    dangerouslySetInnerHTML={{ __html: preset.svg }}
                                />
                            </button>
                        ))}
                    </div>
                    <div className="path-populate-object-rows">
                        {objectSvgs.map((svg, index) => (
                            <div key={index} className="path-populate-object-row">
                                <div className="upload-controls">
                                    <button
                                        type="button"
                                        className="tool-file-btn tool-file-btn--stretch"
                                        onClick={() =>
                                            objectInputRefs.current[index]?.click()
                                        }
                                    >
                                        <FileUp
                                            className="tool-file-btn__icon"
                                            size={18}
                                            aria-hidden
                                        />
                                        <span>
                                            {svg.name || `Choose object SVG ${index + 1}...`}
                                        </span>
                                    </button>
                                    <input
                                        ref={(el) => {
                                            if (el) objectInputRefs.current[index] = el;
                                        }}
                                        type="file"
                                        accept=".svg,image/svg+xml"
                                        className="upload-input"
                                        onChange={handleObjectFileLoad(index)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="tool-action-btn tool-action-btn--danger"
                                    onClick={() => removeObjectSvg(index)}
                                >
                                    <Trash2
                                        className="tool-action-btn__icon"
                                        size={16}
                                        aria-hidden
                                    />
                                    <span>Remove</span>
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="tool-action-btn tool-action-btn--add"
                            onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.multiple = true;
                                input.accept = ".svg,image/svg+xml";
                                input.style.display = "none";
                                input.addEventListener("change", (e) => {
                                    importObjectFiles(
                                        (e.target as HTMLInputElement).files ?? [],
                                    );
                                    document.body.removeChild(input);
                                });
                                document.body.appendChild(input);
                                input.click();
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                importObjectFiles(e.dataTransfer.files);
                            }}
                        >
                            <Plus
                                className="tool-action-btn__icon"
                                size={18}
                                strokeWidth={2.25}
                                aria-hidden
                            />
                            <span>Add object SVG</span>
                        </button>
                    </div>
                    {objectSvgs.length > 0 && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Object placement mode
                                    <select
                                        value={objectPlacementMode}
                                        onChange={(e) =>
                                            setObjectPlacementMode(
                                                e.target.value as
                                                    | "alternating"
                                                    | "random",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="alternating">
                                            Alternating
                                        </option>
                                        <option value="random">Random</option>
                                    </select>
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Object rotation mode
                                    <select
                                        value={objectRotationMode}
                                        onChange={(e) =>
                                            setObjectRotationMode(
                                                e.target.value as
                                                    | "fixed"
                                                    | "path"
                                                    | "radial"
                                                    | "random",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="fixed">Fixed</option>
                                        <option value="path">
                                            Path direction
                                        </option>
                                        <option value="radial">
                                            Radial from center
                                        </option>
                                        <option value="polar-around-point">
                                            Polar around point
                                        </option>
                                        <option value="polar-around-point-inverted">
                                            Polar around point (inverted)
                                        </option>
                                        <option value="random">Random</option>
                                    </select>
                                </label>
                            </div>
                            {(objectRotationMode === "polar-around-point" ||
                                objectRotationMode ===
                                    "polar-around-point-inverted") && (
                                <div className="field-row field-row--two">
                                    <label className="field-label">
                                        Center X (%)
                                        <DebouncedInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.5}
                                            value={polarRotationCenterXPct}
                                            onChange={handleFloat(
                                                setPolarRotationCenterXPct,
                                                0,
                                                100,
                                            )}
                                        />
                                    </label>
                                    <label className="field-label">
                                        Center Y (%)
                                        <DebouncedInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.5}
                                            value={polarRotationCenterYPct}
                                            onChange={handleFloat(
                                                setPolarRotationCenterYPct,
                                                0,
                                                100,
                                            )}
                                        />
                                    </label>
                                </div>
                            )}
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Primitive" icon={Box}>
                    <div className="field-row">
                        <label className="field-label">
                            Primitive
                            <select
                                value={primitiveType}
                                onChange={(e) =>
                                    setPrimitiveType(e.target.value as typeof primitiveType)
                                }
                            >
                                <option value="cube">Cube</option>
                                <option value="geodesic-sphere">
                                    Geodesic Sphere
                                </option>
                                <option value="icosahedron">Icosahedron</option>
                                <option value="torus">Torus</option>
                                <option value="capsule">Capsule</option>
                                <option value="pyramid">Pyramid</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Subdivisions
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={64}
                                step={1}
                                value={subdivisions}
                                onChange={handleInt(setSubdivisions, 1, 64)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={backfaceCulling}
                            onChange={setBackfaceCulling}
                            label="Backface culling"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Rotation" icon={RotateCcw}>
                    <div className="field-row">
                        <label className="field-label">
                            Rotate X (deg)
                            <DebouncedInput
                                type="number"
                                step={0.01}
                                value={rotationXDeg}
                                onChange={handleRotationDeg(setRotationXDeg)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rotate Y (deg)
                            <DebouncedInput
                                type="number"
                                step={0.01}
                                value={rotationYDeg}
                                onChange={handleRotationDeg(setRotationYDeg)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rotate Z (deg)
                            <DebouncedInput
                                type="number"
                                step={0.01}
                                value={rotationZDeg}
                                onChange={handleRotationDeg(setRotationZDeg)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Camera" icon={Camera}>
                    <div className="field-row">
                        <label className="field-label">
                            Camera FOV (deg)
                            <DebouncedInput
                                type="number"
                                min={5}
                                max={170}
                                step={1}
                                value={cameraFovDeg}
                                onChange={handleFloat(setCameraFovDeg, 5, 170)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Camera distance
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={20}
                                step={0.1}
                                value={cameraDistance}
                                onChange={handleFloat(setCameraDistance, 0.1, 20)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            ViewBox X
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={viewBoxX}
                                onChange={handleFloat(setViewBoxX, -100000, 100000)}
                            />
                        </label>
                        <label className="field-label">
                            ViewBox Y
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={viewBoxY}
                                onChange={handleFloat(setViewBoxY, -100000, 100000)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            ViewBox Width
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={viewBoxWidth}
                                onChange={handleFloat(setViewBoxWidth, 1, 100000)}
                            />
                        </label>
                        <label className="field-label">
                            ViewBox Height
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={viewBoxHeight}
                                onChange={handleFloat(setViewBoxHeight, 1, 100000)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Object transform" icon={Settings2}>
                    <div className="field-row">
                        <label className="field-label">
                            Object size (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={500}
                                step={1}
                                value={scale}
                                onChange={handleFloat(setScale, 1, 500)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={depthScaleEnabled}
                            onChange={setDepthScaleEnabled}
                            label="Scale by depth (farther is smaller)"
                            inline
                        />
                    </div>
                    {depthScaleEnabled && (
                        <div className="field-row">
                            <label className="field-label">
                                Depth scale strength
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={depthScaleStrength}
                                    onChange={handleFloat(setDepthScaleStrength, 0, 1)}
                                />
                            </label>
                        </div>
                    )}
                    <div className="field-row">
                        <label className="field-label">
                            Base rotation (deg)
                            <DebouncedInput
                                type="number"
                                step={0.01}
                                value={baseRotationDeg}
                                onChange={handleRotationDeg(setBaseRotationDeg)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Position offset randomness (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={200}
                                step={0.5}
                                value={positionOffsetRandomness}
                                onChange={handleFloat(
                                    setPositionOffsetRandomness,
                                    0,
                                    200,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            X flip (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={flipXPercent}
                                onChange={handleFloat(setFlipXPercent, 0, 100)}
                            />
                        </label>
                        <label className="field-label">
                            Y flip (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={flipYPercent}
                                onChange={handleFloat(setFlipYPercent, 0, 100)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={flipOnBackfacesOnly}
                            onChange={setFlipOnBackfacesOnly}
                            label="Flip only backfaces"
                            inline
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rotation randomness (deg)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={360}
                                step={0.01}
                                value={rotationRandomnessDeg}
                                onChange={handleRotationDeg(setRotationRandomnessDeg, 0, 360)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={groupByFaceOnExport}
                            onChange={setGroupByFaceOnExport}
                            label="Group SVG output by primitive face"
                            inline
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Anchor
                            <select
                                value={anchor}
                                onChange={(e) => setAnchor(e.target.value)}
                                className="anchor-select"
                            >
                                <option value="top-left">Top Left</option>
                                <option value="top">Top</option>
                                <option value="top-right">Top Right</option>
                                <option value="center-left">Center Left</option>
                                <option value="center">Center</option>
                                <option value="center-right">Center Right</option>
                                <option value="bottom-left">Bottom Left</option>
                                <option value="bottom">Bottom</option>
                                <option value="bottom-right">Bottom Right</option>
                            </select>
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "primitive-points-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["primitive-points"]}
                    />
                </CollapsibleToolGroup>
            </div>

            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
